/**
 * Luật thuần của màn Vai trò nền tảng (ADR-0074).
 *
 * tenant-service là chốt chặn thật (`PlatformPermissionGuard`). Ở đây chỉ có
 * những gì giao diện cần để bắt lỗi gõ nhầm sớm, gom danh mục quyền thành lưới,
 * và đọc lỗi `pointer` của server về đúng ô — đủ thuần để test bằng `node:test`.
 *
 * Dùng lại khuôn của `modules/vai-tro/nghiep-vu/vai-tro.ts` nhưng **không dùng
 * chung tệp**: hai hệ mã quyền khác nhau (`TENANT.READ` so với
 * `ocr:document:read`), vai trò ở đây có `is_system`, và xoá vai trò còn người
 * giữ thì bị chặn chứ không gỡ khỏi mọi người. Gộp hai bên vào một chỗ là mời
 * gọi đúng cái lỗi dùng lẫn hai hệ mã mà ADR-0074 mục 7 muốn tránh.
 */
import type { NhomQuyenNenTang, QuyenNenTang, VaiTroNenTang } from '../types.ts';

/** Mẫu `code` theo hợp đồng: chữ hoa đầu, sau đó chữ hoa, số, gạch dưới; 2–63 ký tự. */
export const MAU_MA_VAI_TRO = /^[A-Z][A-Z0-9_]{1,62}$/;

/** Trần độ dài. Mã theo mẫu ở trên; tên đặt trần giao diện, server vẫn là chốt chặn. */
export const DAI_TOI_DA = { code: 63, name: 200 } as const;

export type LoiMa = 'trong' | 'dinhDang' | 'dai';

export function kiemMa(code: string): LoiMa | null {
  const c = code.trim();
  if (!c) return 'trong';
  if (c.length > DAI_TOI_DA.code) return 'dai';
  if (!MAU_MA_VAI_TRO.test(c)) return 'dinhDang';
  return null;
}

export type LoiTenVaiTro = 'trong' | 'dai';

export function kiemTenVaiTro(ten: string): LoiTenVaiTro | null {
  const t = ten.trim();
  if (!t) return 'trong';
  if (t.length > DAI_TOI_DA.name) return 'dai';
  return null;
}

/** Phần phân hệ của mã quyền `<PHÂN_HỆ>.<HÀNH_ĐỘNG>`. */
export const phanHeCuaQuyen = (code: string): string => code.split('.')[0] ?? code;

export interface NhomQuyen {
  nhom: NhomQuyenNenTang;
  quyen: QuyenNenTang[];
}

/**
 * Thứ tự nhóm hiện trong ngăn phân quyền.
 *
 * Cố định theo nghiệp vụ, **không** sắp theo chữ: thứ tự này đi theo thứ tự
 * nhóm ở thanh điều hướng của cổng nền tảng, nên người gán dò theo đúng cái
 * cây màn hình họ đã quen. Nhóm lạ (backend thêm mã mới mà giao diện chưa biết)
 * xuống cuối thay vì mất hút.
 */
export const THU_TU_NHOM: readonly NhomQuyenNenTang[] = [
  // Backend xếp nhóm này đầu danh mục (ADR-0083).
  'MODEL_AI',
  'TENANT',
  'GOI',
  'LICENSE',
  'VIEC',
  'AUDIT',
  'CHINH_SACH',
  'BAO_MAT',
  'LIEN_HE',
  'NHAN_SU',
  'VAI_TRO',
  'KHOA_API',
  'MODULE',
];

/** Gom danh mục quyền theo nhóm, giữ thứ tự server trả trong từng nhóm. */
export function gomTheoNhom(danhMuc: readonly QuyenNenTang[]): NhomQuyen[] {
  const theoNhom = new Map<NhomQuyenNenTang, NhomQuyen>();
  for (const q of danhMuc) {
    const nhom = theoNhom.get(q.nhom);
    if (nhom) nhom.quyen.push(q);
    else theoNhom.set(q.nhom, { nhom: q.nhom, quyen: [q] });
  }
  const viTri = (n: NhomQuyenNenTang) => {
    const i = THU_TU_NHOM.indexOf(n);
    return i === -1 ? THU_TU_NHOM.length : i;
  };
  return [...theoNhom.values()].sort((a, b) => viTri(a.nhom) - viTri(b.nhom));
}

export function batTat(daChon: ReadonlySet<string>, ma: string): Set<string> {
  const moi = new Set(daChon);
  if (moi.has(ma)) moi.delete(ma);
  else moi.add(ma);
  return moi;
}

/**
 * Quyền vai trò đang có mà danh mục không còn.
 *
 * Khác vai trò tenant, nơi mã lạ đến từ module đã gỡ: ở đây danh mục khai trong
 * mã nguồn của backend (ADR-0074 mục 2), nên mã lạ chỉ xảy ra khi backend bỏ
 * một mã mà dòng seed hay vai trò cũ vẫn giữ. Gửi lại chúng là `422`, nên ngăn
 * phân quyền nói ra và bỏ chúng khi lưu.
 */
export function quyenNgoaiDanhMuc(
  quyen: readonly string[],
  danhMuc: readonly QuyenNenTang[],
): string[] {
  const co = new Set(danhMuc.map((q) => q.code));
  return quyen.filter((q) => !co.has(q));
}

/** Hai tập có khác nhau không — để khoá nút lưu khi chưa đổi gì. */
export function khacNhau(a: Iterable<string>, b: Iterable<string>): boolean {
  const sa = new Set(a);
  const sb = new Set(b);
  if (sa.size !== sb.size) return true;
  for (const x of sa) if (!sb.has(x)) return true;
  return false;
}

/**
 * Tập gửi lên theo thứ tự cố định (theo danh mục), để `pointer`
 * `/permissions/{i}` của lỗi `422` tra ngược đúng mã quyền.
 */
export function sapTheoDanhMuc(
  daChon: ReadonlySet<string>,
  danhMuc: readonly QuyenNenTang[],
): string[] {
  return danhMuc.map((q) => q.code).filter((c) => daChon.has(c));
}

/** Một lỗi theo trường trong `errors[]` của Problem Details. */
export interface LoiConTro {
  pointer?: string;
  detail: string;
}

/**
 * Lỗi theo đường dẫn, khoá đã bỏ tiền tố `#`: `{ '/code': '…' }`.
 *
 * Không đòi mã `INVALID_ARGUMENT` như `loiTheoTruong` của `lib/errors`: hợp
 * đồng vai trò nền tảng trả `pointer` cả với `409` (mã trùng) và `422` (quyền
 * không có trong danh mục).
 */
export function loiTheoConTro(chiTiet: unknown): Record<string, string> {
  if (!Array.isArray(chiTiet)) return {};
  const ra: Record<string, string> = {};
  for (const l of chiTiet as LoiConTro[]) {
    if (l && typeof l.pointer === 'string') ra[l.pointer.replace(/^#/, '')] = l.detail;
  }
  return ra;
}

/**
 * Tra `pointer` `/permissions/{i}` về mã quyền đã gửi ở vị trí `i`. Chỉ số
 * ngoài mảng thì bỏ qua — lỗi đó không trỏ được vào ô nào, chỗ gọi tự hiện ở
 * dải chung.
 */
export function loiTheoQuyen(
  loiTruong: Record<string, string>,
  daGui: readonly string[],
): Record<string, string> {
  const ra: Record<string, string> = {};
  for (const [duongDan, thongDiep] of Object.entries(loiTruong)) {
    const m = /^\/permissions\/(\d+)$/.exec(duongDan);
    const ma = m ? daGui[Number(m[1])] : undefined;
    if (ma) ra[ma] = thongDiep;
  }
  return ra;
}

/** Tìm vai trò theo mã hoặc tên, không phân biệt hoa thường và dấu. */
export function locVaiTro(ds: readonly VaiTroNenTang[], tuKhoa: string): VaiTroNenTang[] {
  const bo = (s: string) =>
    s
      .normalize('NFD')
      .replace(/\p{Diacritic}/gu, '')
      .replace(/đ/g, 'd')
      .replace(/Đ/g, 'D')
      .toLowerCase();
  const k = bo(tuKhoa.trim());
  if (!k) return [...ds];
  return ds.filter((r) => bo(r.code).includes(k) || bo(r.name).includes(k));
}

/** Phân hệ mà vai trò có quyền, theo thứ tự xuất hiện, không lặp. */
export function phanHeCuaVaiTro(quyen: readonly string[]): string[] {
  return [...new Set(quyen.map(phanHeCuaQuyen))];
}

/**
 * Xoá được vai trò này không (ADR-0074 mục 7).
 *
 * Hai lý do chặn, và giao diện phải nói đúng lý do: vai trò hệ thống là đường
 * lùi khi cấu hình sai, còn vai trò còn người giữ thì phải gỡ người ra trước để
 * người xoá nhìn thấy ai sẽ mất quyền.
 */
export type LyDoKhongXoa = 'he-thong' | 'con-nguoi-giu' | null;

export function viSaoKhongXoaDuoc(r: VaiTroNenTang): LyDoKhongXoa {
  if (r.is_system) return 'he-thong';
  if (r.user_count > 0) return 'con-nguoi-giu';
  return null;
}

export function xoaDuoc(r: VaiTroNenTang): boolean {
  return viSaoKhongXoaDuoc(r) === null;
}

/** Đổi quyền được khi không phải vai trò hệ thống; đổi tên thì luôn được. */
export function doiQuyenDuoc(r: VaiTroNenTang): boolean {
  return !r.is_system;
}

/** Mã lỗi backend của màn này, để giao diện chọn đúng câu. */
export type LoiVaiTroNenTang =
  | 'ma-da-co'
  | 'dang-dung'
  | 'quyen-khong-co'
  | 'khong-con'
  | 'tu-sua'
  | 'etag-lech'
  | 'thieu-if-match'
  | 'khac';

export function phanLoaiLoi(code: string | undefined, status: number | undefined): LoiVaiTroNenTang {
  if (code === 'PLATFORM_ROLE_ALREADY_EXISTS') return 'ma-da-co';
  if (code === 'PLATFORM_ROLE_IN_USE') return 'dang-dung';
  if (code === 'PERMISSION_NOT_AVAILABLE') return 'quyen-khong-co';
  if (code === 'SELF_ROLE_CHANGE_FORBIDDEN') return 'tu-sua';
  if (code === 'ROLE_VERSION_CONFLICT') return 'etag-lech';
  if (code === 'PRECONDITION_REQUIRED') return 'thieu-if-match';
  // ROLE_NOT_FOUND mang hai trạng thái: 404 là vai trò ở đường dẫn, 422 là mã trong thân (ADR-0041 mục 3)
  if (code === 'ROLE_NOT_FOUND' || code === 'USER_NOT_FOUND') return 'khong-con';
  if (status === 403) return 'khac';
  return 'khac';
}
